import { useState, useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Check, Loader2, Twitter, Linkedin, Facebook, Link2 } from "lucide-react";
import { RichText } from "@/components/RichText";
import { BrandWordmark } from "@/components/BrandWordmark";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import api from "@/lib/api";
import { GUIDES, getGuide } from "@/data/guides";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { CreateathonBanner } from "@/components/CreateathonBanner";

const shareLinks = (url, title) => [
  { name: "X", Icon: Twitter, href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}` },
  { name: "LinkedIn", Icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}` },
  { name: "Facebook", Icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
];

export default function GuideArticle() {
  const { slug } = useParams();
  const staticGuide = getGuide(slug);
  const [dynGuide, setDynGuide] = useState(undefined);
  useEffect(() => {
    setDynGuide(undefined);
    if (!getGuide(slug)) {
      api.get(`/guides/${slug}`).then((r) => setDynGuide(r.data)).catch(() => setDynGuide(null));
    }
  }, [slug]);
  const guide = staticGuide || dynGuide;
  useEffect(() => {
    api.post(`/guides/${slug}/view`).catch(() => {});
  }, [slug]);
  if (!staticGuide && dynGuide === undefined) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center" data-testid="guide-loading">
        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      </div>
    );
  }
  if (!guide) return <Navigate to="/guides" replace />;
  const related = (guide.related || []).map(getGuide).filter(Boolean);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="guide-article-page">
      <CreateathonBanner />
      <Seo title={guide.metaTitle} description={guide.description} path={`/guides/${guide.slug}`} image={guide.image} />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Article",
                headline: guide.title,
                description: guide.description,
                datePublished: guide.date,
                image: `https://www.videoileo.com${guide.image}`,
                author: { "@type": "Organization", name: "Videoileo", url: "https://www.videoileo.com/" },
                publisher: { "@type": "Organization", name: "Videoileo", logo: { "@type": "ImageObject", url: "https://www.videoileo.com/videoileo-icon.png" } },
                mainEntityOfPage: `https://www.videoileo.com/guides/${guide.slug}`,
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Home", item: "https://www.videoileo.com/" },
                  { "@type": "ListItem", position: 2, name: "Guides", item: "https://www.videoileo.com/guides" },
                  { "@type": "ListItem", position: 3, name: guide.title, item: `https://www.videoileo.com/guides/${guide.slug}` },
                ],
              },
            ],
          })}
        </script>
      </Helmet>

      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <BrandWordmark className="h-12" />
          </Link>
          <Link to="/register" data-testid="guide-start-free" className="text-sm bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-md font-medium transition-colors">
            Start free
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-12">
        <Link to="/guides" data-testid="guide-back-link" className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1.5 mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4" /> All guides
        </Link>
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Guide · {guide.readTime}</p>
        <h1 className="font-head text-3xl md:text-4xl tracking-tight font-black mb-8 leading-tight">{guide.title}</h1>
        <img src={guide.image} alt={guide.title} className="w-full rounded-md border border-white/10 mb-6 aspect-[3/2] object-cover" data-testid="guide-cover-image" />

        <div className="flex items-center gap-2 mb-10" data-testid="guide-share-row">
          <span className="text-xs text-zinc-500 mr-1">Share:</span>
          {shareLinks(`https://www.videoileo.com/guides/${guide.slug}`, guide.title).map(({ name, Icon, href }) => (
            <a
              key={name}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Share on ${name}`}
              data-testid={`guide-share-${name.toLowerCase()}`}
              className="h-8 w-8 rounded-md border border-white/10 bg-white/[0.03] hover:bg-white/10 hover:border-blue-500/40 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
            >
              <Icon className="h-3.5 w-3.5" />
            </a>
          ))}
          <button
            onClick={() => {
              navigator.clipboard.writeText(`https://www.videoileo.com/guides/${guide.slug}`);
              toast.success("Link copied — share away!");
            }}
            aria-label="Copy link"
            data-testid="guide-share-copy"
            className="h-8 w-8 rounded-md border border-white/10 bg-white/[0.03] hover:bg-white/10 hover:border-blue-500/40 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
          >
            <Link2 className="h-3.5 w-3.5" />
          </button>
        </div>

        <article className="space-y-10">
          {guide.sections.map((s) => (
            <section key={s.h}>
              <h2 className="font-head text-lg md:text-xl font-semibold mb-3">{s.h}</h2>
              {(s.paras || []).map((p) => (
                <p key={p.slice(0, 40)} className="text-sm md:text-base text-zinc-400 leading-relaxed mb-3"><RichText text={p} /></p>
              ))}
              {s.list && (
                <ul className="space-y-2 mt-2">
                  {s.list.map((item) => (
                    <li key={item} className="text-sm md:text-base text-zinc-400 flex items-start gap-2 leading-relaxed">
                      <Check className="h-4 w-4 text-blue-500 shrink-0 mt-1" /> <span><RichText text={item} /></span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </article>

        <div className="mt-14 border border-blue-500/30 rounded-md bg-blue-600/10 p-6" data-testid="guide-cta">
          <h2 className="font-head text-xl font-bold mb-2">Make your first faceless video free</h2>
          <p className="text-sm text-zinc-400 mb-4 leading-relaxed">
            Videoileo generates the script, voiceover and cinematic visuals, renders the MP4 and schedules it to YouTube — sign up with just your email.
          </p>
          <Link to="/register" className="inline-block text-sm bg-blue-600 hover:bg-blue-500 px-5 py-2.5 rounded-md font-semibold transition-colors">
            Start free — no card required
          </Link>
          <Link to="/pricing" className="inline-block text-sm text-zinc-400 hover:text-white px-4 py-2.5 ml-1 transition-colors">
            See pricing
          </Link>
        </div>

        {related.length > 0 && (
          <div className="mt-12 border-t border-white/10 pt-8">
            <h2 className="font-head text-lg font-semibold mb-4">Keep reading</h2>
            <div className="space-y-2">
              {related.map((r) => (
                <Link key={r.slug} to={`/guides/${r.slug}`} className="block text-sm text-blue-400 hover:text-blue-300 transition-colors">
                  {r.title} →
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12">
          <NewsletterSignup />
        </div>
      </main>

      <footer className="border-t border-white/10 py-10 px-5 text-center text-sm text-zinc-600">
        © 2026 Videoileo · The faceless video studio.
        <span className="mx-2">·</span>
        <Link to="/guides" className="text-zinc-500 hover:text-zinc-300 underline">Guides</Link>
        <span className="mx-2">·</span>
        <Link to="/pricing" className="text-zinc-500 hover:text-zinc-300 underline">Pricing</Link>
      </footer>
    </div>
  );
}
